import React from 'react';
import { ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import type { Page, Component } from '../ComponentEditModal.types';
import { PageSelector } from '../shared/PageSelector';
import { ImageEditorWithCrop } from '../shared/ImageThumbnailWithCrop';
import { lockedSlotAspect } from '../shared/cropUtils';
import type { CropData } from '../shared/ImageCropModal';
import type { Area } from 'react-easy-crop';

export interface ImageCardItem {
  id: string;
  imageUrl: string;
  cloudfrontUrl?: string;
  alt?: string;
  title: string;
  description?: string;
  url?: string;
  target?: string;
  order: number;
  crop?: CropData;
  button?: {
    text: string;
    style: string;
  };
}

interface ImageCardItemCardProps {
  item: ImageCardItem;
  index: number;
  totalItems: number;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
  onUpdate: (field: string, value: any) => void;
  onUpdateFields?: (fields: Record<string, any>) => void;
  onRemove: () => void;
  onMove: (direction: 'up' | 'down') => void;
  onUpload: () => void;
  aspectRatio?: string;
}

export const ImageCardItemCard: React.FC<ImageCardItemCardProps> = ({
  item,
  index,
  totalItems,
  pages,
  isLoadingPages,
  currentPageComponents,
  onUpdate,
  onUpdateFields,
  onRemove,
  onMove,
  onUpload,
  aspectRatio,
}) => {
  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <div className="flex justify-between items-start mb-3">
        <span className="text-sm font-medium text-gray-700">Card {index + 1}</span>
        <div className="flex space-x-1">
          <button
            type="button"
            onClick={() => onMove('up')}
            disabled={index === 0}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
            title="Move up"
          >
            <ChevronUp size={16} />
          </button>
          <button
            type="button"
            onClick={() => onMove('down')}
            disabled={index === totalItems - 1}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
            title="Move down"
          >
            <ChevronDown size={16} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="p-1 text-red-400 hover:text-red-600"
            title="Delete"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {/* Image with Crop */}
        <ImageEditorWithCrop
          imageUrl={item.imageUrl}
          cloudfrontUrl={item.cloudfrontUrl}
          crop={item.crop}
          onUrlChange={url => onUpdate('imageUrl', url)}
          onUpload={onUpload}
          onRemove={() => {
            if (onUpdateFields) {
              onUpdateFields({ imageUrl: '', cloudfrontUrl: '', crop: undefined });
            } else {
              onUpdate('imageUrl', '');
              onUpdate('cloudfrontUrl', '');
              onUpdate('crop', undefined);
            }
          }}
          onCropChange={(crop: CropData, _croppedAreaPixels: Area) => {
            onUpdate('crop', crop);
          }}
          label="Image"
          cropMode="frame"
          lockedAspect={lockedSlotAspect(aspectRatio || 'square')}
        />

        {/* Alt Text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Alt Text</label>
          <input
            type="text"
            value={item.alt || ''}
            onChange={e => onUpdate('alt', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Descriptive alt text"
            maxLength={200}
          />
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
          <input
            type="text"
            value={item.title || ''}
            onChange={e => onUpdate('title', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Card Title"
            maxLength={100}
            required
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <textarea
            value={item.description || ''}
            onChange={e => onUpdate('description', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Card description..."
            rows={3}
            maxLength={500}
          />
        </div>

        {/* Link URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Link URL (optional)
          </label>
          <PageSelector
            value={item.url || ''}
            onChange={url => onUpdate('url', url)}
            pages={pages}
            isLoadingPages={isLoadingPages}
            currentPageComponents={currentPageComponents}
          />
        </div>

        {item.url && (
          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={item.target === '_blank'}
                onChange={e => onUpdate('target', e.target.checked ? '_blank' : '_self')}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Open in new tab</span>
            </label>
          </div>
        )}

        {/* Button */}
        <ButtonSection item={item} onUpdate={onUpdate} />
      </div>
    </div>
  );
};

// Sub-component for button

interface ButtonSectionProps {
  item: ImageCardItem;
  onUpdate: (field: string, value: any) => void;
}

const ButtonSection: React.FC<ButtonSectionProps> = ({ item, onUpdate }) => {
  const handleToggleButton = (checked: boolean) => {
    if (checked) {
      onUpdate('button', {
        text: 'Learn More',
        style: 'primary',
      });
    } else {
      onUpdate('button', undefined);
    }
  };

  return (
    <div className="border-t pt-3 mt-3">
      <label className="flex items-center space-x-2 mb-2">
        <input
          type="checkbox"
          checked={!!item.button}
          onChange={e => handleToggleButton(e.target.checked)}
          className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
        />
        <span className="text-sm font-medium text-gray-700">Show Button</span>
      </label>

      {item.button && (
        <div className="space-y-3 pl-6 border-l-2 border-gray-200">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Button Text</label>
            <input
              type="text"
              value={item.button?.text || ''}
              onChange={e => onUpdate('button', { ...item.button, text: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Learn More"
              maxLength={30}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Button Style</label>
            <select
              value={item.button?.style || 'primary'}
              onChange={e => onUpdate('button', { ...item.button, style: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
              <option value="outline">Outline</option>
              <option value="ghost">Ghost</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};
